/**
 * De onde a tela Natal 2026 tira os dados: só do serviço `services/natal-2026` (loopback 8109, nginx com
 * sessão do BI). Sem arquivo estático de reserva: um painel de metas com número velho engana — se o
 * serviço não responder, a tela diz isso.
 */
import type { Briefing, CatalogoTerceiro, Decisao, MsgChat, NatalData, PedidosAnuncio } from './types'

const API = '/api/natal-2026'

export async function carregarNatal(fresco = false): Promise<NatalData> {
  const res = await fetch(`${API}/dados${fresco ? '?fresco=1' : ''}`, { credentials: 'include' })
  if (!res.ok) {
    const corpo = (await res.json().catch(() => ({}))) as { detail?: string }
    throw new Error(corpo.detail || `serviço Natal 2026 respondeu ${res.status}`)
  }
  return (await res.json()) as NatalData
}

/** Situação da montagem (para recarregar quando a atualização em segundo plano terminar). */
export type StatusNatal = {
  montando: boolean; ultimo_erro: string | null; rodando_catalogos: string | null; erros_rotinas?: Record<string, string | null>
}

export async function statusNatal(): Promise<StatusNatal> {
  const res = await fetch(`${API}/status`, { credentials: 'include' })
  if (!res.ok) throw new Error(`http ${res.status}`)
  return (await res.json()) as StatusNatal
}

export async function pedirAtualizacao(): Promise<string> {
  const res = await fetch(`${API}/atualizar`, { method: 'POST', credentials: 'include' })
  const corpo = (await res.json().catch(() => ({}))) as { mensagem?: string; detail?: string }
  if (!res.ok) throw new Error(corpo.detail || `http ${res.status}`)
  return corpo.mensagem || 'atualização disparada'
}

async function postar<T>(caminho: string, corpo: unknown): Promise<T> {
  const res = await fetch(`${API}${caminho}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corpo),
  })
  const j = (await res.json().catch(() => ({}))) as T & { detail?: string }
  if (!res.ok) throw new Error(j.detail || `http ${res.status}`)
  return j
}

/** Aprovar/rejeitar uma sugestão da fila. NÃO muda preço no marketplace: só registra no log do painel. */
export async function registrarDecisao(d: {
  sku: string; anuncio: string; loja: string; acao: 'aprovar' | 'rejeitar'; preco_de: number; preco_para: number
  mc_de: number | null; mc_para: number | null; motivo: string
}): Promise<Decisao> {
  return (await postar<{ decisao: Decisao }>('/decisoes', { ...d, tipo: 'preco' })).decisao
}

/** Marca a situação de um catálogo de terceiros (registro local do painel). */
export async function marcarCatalogo(catalogo_id: string, status: CatalogoTerceiro['situacao']): Promise<void> {
  await postar('/catalogos/status', { catalogo_id, status })
}

/** Relê os catálogos agora (nossos: price_to_win e vencedor; terceiros: vendedores, preço e visitas). Só leitura no ML. */
export async function atualizarCatalogos(alvo: 'nossos' | 'terceiros'): Promise<{ rodando: boolean; mensagem: string }> {
  return await postar<{ rodando: boolean; mensagem: string }>(`/catalogos/atualizar?alvo=${alvo}`, {})
}

/** `skus`: numa variação da Shopee, só os pedidos dos SKUs de Natal dela (o anúncio tem outras cores). */
export async function carregarPedidosAnuncio(loja: string, anuncio: string, periodo: '7d' | 'temp', skus: string[] = [],
  recorte: { de?: string; ate?: string; variacao?: string | null } = {}): Promise<PedidosAnuncio> {
  const extra = Object.entries(recorte).filter(([, v]) => v).map(([k, v]) => `&${k}=${encodeURIComponent(v as string)}`).join('')
  const filtro = (skus.length ? `&sku=${encodeURIComponent(skus.join(','))}` : '') + extra
  const res = await fetch(`${API}/anuncios/${encodeURIComponent(loja)}/${encodeURIComponent(anuncio)}/pedidos?periodo=${periodo}${filtro}`, { credentials: 'include' })
  const j = (await res.json().catch(() => ({}))) as PedidosAnuncio & { detail?: string }
  if (!res.ok) throw new Error(j.detail || `http ${res.status}`)
  return j
}

/** Erro do serviço com o status http (o chat distingue 503 = IA indisponível de falha comum). */
export class ErroServico extends Error {
  status: number
  constructor(msg: string, status: number) { super(msg); this.status = status }
}

export async function carregarBriefing(dia?: string): Promise<Briefing> {
  const res = await fetch(`${API}/briefing${dia ? `?dia=${dia}` : ''}`, { credentials: 'include' })
  const j = (await res.json().catch(() => ({}))) as Briefing & { detail?: string }
  if (!res.ok) throw new Error(j.detail || `http ${res.status}`)
  return j
}

export async function perguntarBriefing(pergunta: string, historico: MsgChat[]): Promise<{ resposta: string; modelo: string; selo?: string }> {
  const res = await fetch(`${API}/briefing/conversa`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pergunta, historico }),
  })
  const j = (await res.json().catch(() => ({}))) as { resposta: string; modelo: string; selo?: string; detail?: string }
  if (!res.ok) throw new ErroServico(j.detail || `http ${res.status}`, res.status)
  return j
}

export async function enviarShopping(arquivo: File, categoria: 'arvore' | 'pisca', inicio: string, fim: string) {
  const res = await fetch(`${API}/mercado/shopping?categoria=${categoria}&inicio=${inicio}&fim=${fim}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: await arquivo.arrayBuffer(),
  })
  const j = (await res.json().catch(() => ({}))) as { linhas: number; unidades: number; skus_com_recorte: number; detail?: string }
  if (!res.ok) throw new Error(j.detail || `http ${res.status}`)
  return j
}

/** "shopping de preço 011026 a 071026 categoria árvore.xlsx" → categoria e período (editáveis na tela). */
export function lerNomeExportacao(nome: string): { categoria: 'arvore' | 'pisca' | null; inicio: string; fim: string } {
  const n = nome.toLowerCase()
  const cat = /arvor|árvor/.test(n) ? 'arvore' : /pisca|ilumina/.test(n) ? 'pisca' : null
  const m = n.match(/(\d{2})(\d{2})(\d{2})\D+(\d{2})(\d{2})(\d{2})/)
  const iso = (d: string, mo: string, a: string) => `20${a}-${mo}-${d}`
  return { categoria: cat, inicio: m ? iso(m[1], m[2], m[3]) : '', fim: m ? iso(m[4], m[5], m[6]) : '' }
}

/** Participação de cada loja na meta (fração 0–1; soma 100%). Grava no serviço e entra no Log de decisões. */
export async function ajustarParticipacao(valores: Record<string, number>, motivo: string): Promise<Decisao> {
  return (await postar<{ decisao: Decisao }>('/ajustes/participacao', { valores, motivo })).decisao
}

/** Container recebido (data real em que entrou no estoque) ou nova previsão de chegada. */
export async function ajustarContainer(embarque: string, situacao: 'recebido' | 'previsto', data: string, motivo: string): Promise<Decisao> {
  return (await postar<{ decisao: Decisao }>('/ajustes/container', { embarque, situacao, data, motivo })).decisao
}

/** Links de concorrentes do ML por SKU (lista editada pela planilha). */
export type LinkConcorrente = { sku: string; descricao: string; link: string; tipo: 'catalogo' | 'anuncio' | 'user_product' | 'desconhecido'
  mediana: number | null; n: number; coletado_em: string | null; fonte: string | null; situacao: string; automatico: boolean }
export type PreviaLinks = { gravado: boolean; mantidos: number; precos_informados: number
  novos: { sku: string; link: string }[]; removidos: { sku: string; link: string }[]; recusadas: { linha: number; sku: string; link: string; motivo: string }[] }

export const URL_EXPORTAR_LINKS = `${API}/links/exportar`

export async function carregarLinks(): Promise<{ linhas: LinkConcorrente[]; atualizado_em: string | null }> {
  const res = await fetch(`${API}/links`, { credentials: 'include' })
  if (!res.ok) throw new Error(`http ${res.status}`)
  return await res.json()
}

/** Planilha editada: confirmar=false só devolve a prévia; true grava (substitui a lista). */
export async function importarLinks(arquivo: File, confirmar: boolean): Promise<PreviaLinks> {
  const res = await fetch(`${API}/links/importar?confirmar=${confirmar ? 1 : 0}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: await arquivo.arrayBuffer(),
  })
  const j = (await res.json().catch(() => ({}))) as PreviaLinks & { detail?: string }
  if (!res.ok) throw new Error(j.detail || `http ${res.status}`)
  return j
}

/** Lê agora os preços dos links de catálogo pela API do ML. */
export async function atualizarLinks(): Promise<string> {
  return (await postar<{ mensagem: string }>('/links/atualizar', {})).mensagem
}
